import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { CenteredLoader } from '@/components_deprecated'
import type { FilterChildren } from '@/components_deprecated/FilterPopup'
import { FilterPopup } from '@/components_deprecated/FilterPopup'
import {
  FilterOptionsStack,
  StyledFilterCheckbox,
} from '@/components_deprecated/FilterPopup/filter-option-styles'
import {
  FilterCheckmarksWrapper,
  Status,
  StatusButton,
} from '@/components_deprecated/FilterPopup/components'
import xor from 'lodash/xor'
import React from 'react'
import { useOnErrorAuto } from '@/utils/hooks'
import { ALL_ISSUE_CATEGORIES_SHALLOW_QUERY } from '../queries'
import { ISSUE_INVOLVEMENT_FILTERS, ISSUE_STATUS_FILTERS } from '../types'
import type {
  IssueInvolvementFilter,
  IssueStatusFilter,
  IssuesTableFilter,
} from '../types'

const ISSUE_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const

const toggleMultiSelect = <T extends string>(
  value: T,
  current: readonly T[],
  allValues: readonly T[]
): T[] => {
  const base = current.length === 0 ? allValues : current
  const next = base.includes(value)
    ? base.filter(v => v !== value)
    : [...base, value]
  return next.length === allValues.length ? [] : next
}
import type {
  AllIssueCategoriesShallowQuery,
  IssueSeverity,
} from '../types.graphl'

export const IssuePopupFilter: React.FC<FilterChildren<IssuesTableFilter>> = ({
  filter,
  onUpdate,
}) => {
  const translations = useTranslate({
    status: 'common.status',
    archived: 'common.archived',
    categories: 'common.categories',
    all: 'common.all',
    showManuallyCreatedIssuesOnly: 'issues.show-manually-created-issues-only',

    open: 'common.open-alt',
    closed: 'common.closed',
    started: 'issues.started',

    severities: 'common.severities',

    low: 'common.low',
    medium: 'common.medium',
    high: 'common.high',

    involvement: 'issues.involvement',
    involved: 'issues.involved',
    notInvolved: 'issues.not-involved',
  })
  const onErrorAuto = useOnErrorAuto()
  const { data: categoriesData, loading: categoriesLoading } =
    useQuery<AllIssueCategoriesShallowQuery>(
      ALL_ISSUE_CATEGORIES_SHALLOW_QUERY,
      {
        onError: onErrorAuto(),
      }
    )

  const categories =
    categoriesData?.allIssueCategories.edges.map(edge => edge.node) ?? []
  const allStatusesSelected = filter.statuses.length === 0
  const allSeveritiesSelected = filter.severities.length === 0
  const allCategoriesSelected = filter.excludeCategories.length === 0
  const allInvolvementSelected = filter.involvement.length === 0

  function handleClickStatus(status: IssueStatusFilter) {
    return () =>
      onUpdate({
        statuses: toggleMultiSelect(
          status,
          filter.statuses,
          ISSUE_STATUS_FILTERS
        ),
      })
  }

  function toggleArchived(archived: boolean) {
    return () => {
      onUpdate({
        archived,
      })
    }
  }

  function toggleCategory(id: string) {
    return () => {
      const allCategoryIds = categories.map(cat => cat.id)

      if (filter.excludeCategories.length === 0) {
        onUpdate({
          excludeCategories: allCategoryIds.filter(catId => catId !== id),
        })
      } else {
        const newExcludeCategories = xor(filter.excludeCategories, [id])

        if (newExcludeCategories.length === allCategoryIds.length) {
          onUpdate({
            excludeCategories: [],
          })
        } else {
          onUpdate({
            excludeCategories: newExcludeCategories,
          })
        }
      }
    }
  }

  function handleClickSeverity(severity: IssueSeverity) {
    return () =>
      onUpdate({
        severities: toggleMultiSelect(
          severity,
          filter.severities,
          ISSUE_SEVERITIES
        ),
      })
  }

  function handleClickInvolvement(value: IssueInvolvementFilter) {
    return () =>
      onUpdate({
        involvement: toggleMultiSelect(
          value,
          filter.involvement,
          ISSUE_INVOLVEMENT_FILTERS
        ),
      })
  }

  return (
    <FilterPopup>
      <h4>{translations.status}</h4>

      <Status statusCount={3}>
        <StatusButton
          active={allStatusesSelected || filter.statuses.includes('OPEN')}
          onClick={handleClickStatus('OPEN')}
        >
          {translations.open}
        </StatusButton>
        <StatusButton
          active={allStatusesSelected || filter.statuses.includes('STARTED')}
          onClick={handleClickStatus('STARTED')}
        >
          {translations.started}
        </StatusButton>
        <StatusButton
          active={allStatusesSelected || filter.statuses.includes('CLOSED')}
          onClick={handleClickStatus('CLOSED')}
        >
          {translations.closed}
        </StatusButton>
      </Status>

      <h4>{translations.severities}</h4>

      <Status statusCount={3}>
        <StatusButton
          active={allSeveritiesSelected || filter.severities.includes('LOW')}
          onClick={handleClickSeverity('LOW')}
        >
          {translations.low}
        </StatusButton>
        <StatusButton
          active={allSeveritiesSelected || filter.severities.includes('MEDIUM')}
          onClick={handleClickSeverity('MEDIUM')}
        >
          {translations.medium}
        </StatusButton>
        <StatusButton
          active={allSeveritiesSelected || filter.severities.includes('HIGH')}
          onClick={handleClickSeverity('HIGH')}
        >
          {translations.high}
        </StatusButton>
      </Status>

      <h4>{translations.archived}</h4>
      <Status statusCount={2}>
        <StatusButton
          active={filter.archived === false}
          onClick={toggleArchived(false)}
        >
          {translations.open}
        </StatusButton>
        <StatusButton
          active={filter.archived === true}
          onClick={toggleArchived(true)}
        >
          {translations.archived}
        </StatusButton>
      </Status>

      <h4>{translations.involvement}</h4>
      <Status statusCount={2}>
        <StatusButton
          active={
            allInvolvementSelected || filter.involvement.includes('INVOLVED')
          }
          onClick={handleClickInvolvement('INVOLVED')}
        >
          {translations.involved}
        </StatusButton>
        <StatusButton
          active={
            allInvolvementSelected ||
            filter.involvement.includes('NOT_INVOLVED')
          }
          onClick={handleClickInvolvement('NOT_INVOLVED')}
        >
          {translations.notInvolved}
        </StatusButton>
      </Status>

      <h4>{translations.categories}</h4>

      <FilterCheckmarksWrapper>
        {categoriesLoading && (
          <CenteredLoader marginTop="1rem" marginBottom="1rem" />
        )}
        <FilterOptionsStack>
          <StyledFilterCheckbox
            checked={allCategoriesSelected}
            label={translations.all}
            boxWidth="24px"
            boxHeight="24px"
            fontSize="0.8rem"
            onChange={() =>
              onUpdate({
                excludeCategories: [],
              })
            }
          />
          {categories.map(category => {
            const checked =
              filter.excludeCategories.length > 0 &&
              !filter.excludeCategories.includes(category.id)
            return (
              <StyledFilterCheckbox
                key={category.id}
                checked={checked}
                label={category.name}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleCategory(category.id)}
              />
            )
          })}
        </FilterOptionsStack>
      </FilterCheckmarksWrapper>
    </FilterPopup>
  )
}
